import { useCallback, useEffect, useRef, useState } from 'react';
import { useAssignReferrer } from '@xhype/card-app-sdk';
import * as Sentry from '@sentry/react';

const REFERRAL_CODE_KEY = 'pendingReferralCode';

/**
 * Hook to manage referral code from URL and localStorage
 * Captures referral code from URL params and stores it until user is registered
 */
export const useReferralCode = () => {
  const [isAssigning, setIsAssigning] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const hasCaptured = useRef(false);

  // Mutation to assign referrer
  const assignReferrer = useAssignReferrer({
    onSuccess: () => {
      console.log('Referrer assigned successfully');
      clearReferralCode();
      setIsAssigning(false);
    },
    onError: (error: any) => {
      console.error('Error assigning referrer:', error);
      setError(error?.response?.data?.msg || 'Failed to assign referrer');
      setIsAssigning(false);
      Sentry.captureException({
        message: 'Error assigning referrer',
        extra: { error },
      });
    },
  });

  /**
   * Capture referral code from URL and store it in localStorage
   */
  const captureReferralCode = useCallback(() => {
    // Prevent multiple captures
    if (hasCaptured.current) {
      console.log('[useReferralCode] ⏭️ Already captured, skipping...');
      return;
    }

    try {
      console.log(
        '[useReferralCode] Attempting to capture referral code from URL',
      );
      console.log('[useReferralCode] Current URL:', window.location.href);

      const urlParams = new URLSearchParams(window.location.search);
      const referralCode = urlParams.get('referral');

      console.log(
        '[useReferralCode] URL params:',
        Object.fromEntries(urlParams.entries()),
      );
      console.log('[useReferralCode] Referral code from URL:', referralCode);

      if (referralCode) {
        hasCaptured.current = true;
        localStorage.setItem(REFERRAL_CODE_KEY, referralCode);
        console.log(
          '[useReferralCode] ✅ Referral code saved to localStorage:',
          referralCode,
        );

        // Verify it was saved
        const saved = localStorage.getItem(REFERRAL_CODE_KEY);
        console.log(
          '[useReferralCode] Verification - Code in localStorage:',
          saved,
        );

        // Clean URL without reloading
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
        console.log('[useReferralCode] URL cleaned to:', newUrl);
      } else {
        console.log('[useReferralCode] ⚠️ No referral code found in URL');
      }
    } catch (error) {
      console.error(
        '[useReferralCode] ❌ Error capturing referral code:',
        error,
      );
      Sentry.captureException({
        message: 'Error capturing referral code',
        extra: { error },
      });
    }
  }, []);

  /**
   * Get stored referral code from localStorage
   */
  const getReferralCode = useCallback((): string | null => {
    try {
      const code = localStorage.getItem(REFERRAL_CODE_KEY);
      console.log('[useReferralCode] Getting code from localStorage:', code);
      return code;
    } catch (error) {
      console.error('[useReferralCode] Error getting referral code:', error);
      return null;
    }
  }, []);

  /**
   * Clear referral code from localStorage
   */
  const clearReferralCode = useCallback(() => {
    try {
      localStorage.removeItem(REFERRAL_CODE_KEY);
      console.log('Referral code cleared');
    } catch (error) {
      console.error('Error clearing referral code:', error);
    }
  }, []);

  /**
   * Assign referrer to user after registration
   */
  const assignReferralCode = useCallback(
    async (userId: number): Promise<void> => {
      const referralCode = getReferralCode();

      if (!referralCode) {
        console.log(
          '[useReferralCode] No referral code found, skipping assignment',
        );
        return;
      }

      if (!userId) {
        console.error(
          '[useReferralCode] No userId provided, cannot assign referral',
        );
        return;
      }

      try {
        setIsAssigning(true);
        setError(undefined);

        await assignReferrer.mutateAsync({
          userId,
          referralCode,
        });

        console.log('[useReferralCode] ✅ Referral code assigned successfully');
      } catch (err) {
        console.error('[useReferralCode] ❌ Error in assignReferralCode:', err);
        throw err; // Re-throw to let caller handle the error
      } finally {
        setIsAssigning(false);
      }
    },
    [getReferralCode, assignReferrer],
  );

  // Capture referral code on mount
  useEffect(() => {
    console.log('[useReferralCode] Hook mounted, capturing referral code...');
    captureReferralCode();
  }, [captureReferralCode]);

  return {
    captureReferralCode,
    getReferralCode,
    clearReferralCode,
    assignReferralCode,
    isAssigning,
    error,
  };
};
